import { useCallback, useEffect, useRef, useState } from 'react';
import { runAfterInteractions } from '../utils/afterInteractions';
import type { TonyteActivity, TonyteUser } from '../types';
import {
  EMPTY_HISTORY,
  MIN_TOP_PICKS,
  rankTopPicks,
  type RankedEvent,
  type RecommendationHistory,
  type TopPicksMode,
} from '../utils/eventRecommendations';
import { loadUserEventSignals, resolveEventHistory, type UserEventSignals } from '../services/userEventSignals';

export type TopPicksStatus = 'loading' | 'ready' | 'empty' | 'error';

type Args = {
  userId: string | null;
  profile: TonyteUser | null;
  // False while the profile document is still loading, so a signed-in user
  // with interests is never briefly labelled "Popular Near You".
  profileReady: boolean;
  candidates: TonyteActivity[];
  excludeEventIds: string[];
  cityKey: string | null;
  userCoords: { latitude: number; longitude: number } | null;
  // How many ranked events to keep, after diversification. Defaults to
  // rankTopPicks' own DEFAULT_TOP_PICKS_LIMIT (right for the carousel); the
  // "See all" screen asks for more.
  limit?: number;
};

type SignalsState =
  | { status: 'loading' }
  // The history couldn't be loaded in time (or at all): rank without it. A
  // late arrival replaces this with 'ready' and re-ranks.
  | { status: 'skipped' }
  | { status: 'ready'; history: RecommendationHistory; excludeIds: string[]; blockedUserIds: string[] };

// How long the carousel waits on the user's history before showing a
// profile/location/popularity-only ranking instead of a skeleton.
const SIGNALS_WAIT_MS = 2500;

// Coordinates are rounded (~110 m) so a GPS tick doesn't re-rank the carousel.
const roundCoord = (value: number) => Math.round(value * 1000) / 1000;

export function useTopPicks({
  userId,
  profile,
  profileReady,
  candidates,
  excludeEventIds,
  cityKey,
  userCoords,
  limit,
}: Args) {
  const [signals, setSignals] = useState<SignalsState>(() => (userId ? { status: 'loading' } : { status: 'skipped' }));
  const [result, setResult] = useState<{ items: RankedEvent[]; mode: TopPicksMode } | null>(null);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // The history lookup needs the pool only to avoid re-reading events already
  // in memory; it must not re-run every time the pool array changes identity.
  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;
  const signalsUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setSignals({ status: 'skipped' });
      return undefined;
    }
    let cancelled = false;
    // A manual refresh keeps the current history on screen while it reloads;
    // a different account must never inherit the previous one's.
    const sameUser = signalsUserRef.current === userId;
    signalsUserRef.current = userId;
    setSignals(prev => (prev.status === 'ready' && sameUser ? prev : { status: 'loading' }));
    const giveUp = setTimeout(() => {
      if (!cancelled) setSignals(prev => (prev.status === 'loading' ? { status: 'skipped' } : prev));
    }, SIGNALS_WAIT_MS);

    (async () => {
      try {
        const raw: UserEventSignals = await loadUserEventSignals(userId, reloadKey > 0);
        const history = await resolveEventHistory(raw, candidatesRef.current);
        if (cancelled) return;
        setSignals({
          status: 'ready',
          history,
          excludeIds: [...raw.interestedIds, ...raw.joinedIds],
          blockedUserIds: raw.blockedUserIds,
        });
      } catch (error) {
        if (__DEV__) console.warn('[Tonyte][RN][TopPicks] signals unavailable, ranking without history', error);
        if (!cancelled) setSignals({ status: 'skipped' });
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(giveUp);
    };
  }, [userId, reloadKey]);

  // Picks belong to one account; never show the previous user's after a switch.
  useEffect(() => {
    setResult(null);
    setFailed(false);
  }, [userId]);

  const excludeKey = excludeEventIds.join('|');
  const latitude = userCoords ? roundCoord(userCoords.latitude) : null;
  const longitude = userCoords ? roundCoord(userCoords.longitude) : null;
  const inputsReady = (!userId || profileReady) && signals.status !== 'loading';

  const history = signals.status === 'ready' ? signals.history : EMPTY_HISTORY;
  const signalExcludeIds = signals.status === 'ready' ? signals.excludeIds : undefined;
  const blockedUserIds = signals.status === 'ready' ? signals.blockedUserIds : undefined;

  useEffect(() => {
    if (!inputsReady) return undefined;
    // Ranking runs after interactions settle so scoring a few hundred
    // candidates never competes with the feed's first render or a scroll.
    const handle = runAfterInteractions(() => {
      try {
        const ranked = rankTopPicks({
          candidates,
          userId,
          profile,
          history,
          excludeEventIds: [...(excludeKey ? excludeKey.split('|') : []), ...(signalExcludeIds ?? [])],
          blockedUserIds,
          userCoords: latitude !== null && longitude !== null ? { latitude, longitude } : null,
          cityKey,
          limit,
        });
        setResult({ items: ranked.items, mode: ranked.mode });
        setFailed(false);
      } catch (error) {
        console.warn('[Tonyte][RN][TopPicks] ranking failed', error);
        setFailed(true);
      }
    });
    return () => handle.cancel();
    // `candidates` identity changes on every feed refresh, which is exactly
    // when a re-rank is wanted.
  }, [blockedUserIds, candidates, cityKey, excludeKey, history, inputsReady, latitude, longitude, limit, profile, signalExcludeIds, userId]);

  const refresh = useCallback(() => {
    setFailed(false);
    setReloadKey(key => key + 1);
  }, []);

  let status: TopPicksStatus;
  if (failed) status = 'error';
  else if (!result) status = 'loading';
  else status = result.items.length >= MIN_TOP_PICKS ? 'ready' : 'empty';

  return {
    status,
    items: status === 'ready' ? result!.items : [],
    mode: result?.mode ?? 'popular',
    refresh,
  };
}
